import React, { useState } from 'react'
import {
  Box,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Modal,
  Grid,
  CircularProgress,
} from '@mui/material'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { NexusService } from 'global-apis/nexus-service'
import { useTheme } from '../../../../../utils/themeContext'
import { getColorsForTheme } from '../../../../../global-styles/clientColors'
import toast from 'react-hot-toast'


interface ConnectedAccountsProps {
  reservation: any
}

const ConvertToGalpinModal = ({ isOpen, onClose, onConfirm, loading }) => {
  const { currentTheme } = useTheme()
  const themeColors = getColorsForTheme(currentTheme)

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,
        }}
      >
        <Grid container spacing={2}>
          <Grid>
            <Typography variant="h6" component="h2">
              Convert to Galpin
            </Typography>
          </Grid>
          <Grid>
            <Typography variant="body2">
              Are you sure you want to convert this customer to Galpin? This action cannot be undone.
            </Typography>
          </Grid>
          <Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={onConfirm}
                disabled={loading}
                sx={{
                  ...(currentTheme === 'galpin' && {
                    backgroundColor: themeColors.buttonColor,
                    color: 'black',
                    '&:hover': {
                      backgroundColor: themeColors.buttonHover,
                    },
                  }),
                }}
              >
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  'Confirm'
                )}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  )
}

export const RelatedAccounts: React.FC<ConnectedAccountsProps> = ({
  reservation
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const queryClient = useQueryClient()
  const { currentTheme } = useTheme()
  const themeColors = getColorsForTheme(currentTheme)
  const customerId = reservation?.customer?.id
  const customerPhone= reservation?.customerInfo?.phone
  const reservationId= reservation?.id

  // Fetch list of clients to map client IDs to names
  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: NexusService.getListClients,
    placeholderData: [],
  })

  // Create a mapping function from client ID to client name
  const getClientName = (clientId: string) => {
    const client = clients?.find((client) => client.id === clientId)
    return client?.name || clientId || 'Unknown'
  }

  // Fetch related accounts based on phone number
  const { data: relatedAccounts, isLoading, error } = useQuery({
    queryKey: ['related-accounts', customerPhone],
    queryFn:
    async () => {
      if (!customerPhone) return []

      try {
        const response = await NexusService.getAllCustomersByPhone(customerPhone)
        return (response as { customersList: any[] }).customersList
          ?.map((customer) => ({
            customerId: customer.customerId,
            clientId: customer.clientId || 'Unknown',
            accountNumber: customer.accountNumber || 'N/A',
          })) || []
      } catch (error) {
        console.error('Error fetching related accounts:', error)
        return []
      }
    },
    enabled: !!customerPhone,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  }
  )

    // Exclude current customer
    const  filteredRelatedAccounts = relatedAccounts?.filter((customer) => customer.customerId !== customerId)

  // Convert to Galpin mutation
  const convertToGalpinMutation = useMutation({
    mutationFn: () => NexusService.convertToGalpinTesla(reservationId),
      onSuccess: () => {
        toast.success('Successfully converted to Galpin')
        setIsModalOpen(false)
        queryClient.invalidateQueries({queryKey: ['related-accounts']})
        queryClient.invalidateQueries({queryKey: ['/reservation']})
      },
      onError: (error) => {
        toast.error('Failed to convert to Galpin')
        console.error('Convert to Galpin error:', error)
      },
    }
  )

  // Check if any related customer has Galpin client ID or name

  const hasGalpinCustomer = relatedAccounts?.some((customer) => {
    const clientName = getClientName(customer.clientId)
    return clientName?.toLowerCase().includes('galpin')
  })

  // Don't render anything if loading, no phone number, or error occurred
  if (isLoading || !customerPhone || error) {
    if (error) {
      console.error('related accounts error:', error)
    }
    return null
  }

  // Render the component if we have connected customers OR if we need to show Convert to Galpin button
  const shouldRender = filteredRelatedAccounts?.length > 0 || (!hasGalpinCustomer && !reservation?.vehicleId)

  if (!shouldRender) {
    return null
  }

  return (
    <Card sx={{ mb: 3, p: 2 }}>
      <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
        Related Accounts
      </Typography>

      {filteredRelatedAccounts?.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Client Name</TableCell>
                <TableCell>Account Number</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRelatedAccounts.map((customer) => (
                <TableRow key={customer.customerId}>
                  <TableCell>{getClientName(customer.clientId)}</TableCell>
                  <TableCell>
                    <Link
                      to={`/customers/${customer.customerId}`}
                      style={{
                        color: themeColors.buttonColor,
                        textDecoration: 'none',
                      }}
                    >
                      {customer.accountNumber}
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!hasGalpinCustomer && !reservation?.vehicleId && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setIsModalOpen(true)}
            sx={{
              ...(currentTheme === 'galpin' && {
                backgroundColor: themeColors.buttonColor,
                color: 'black',
                '&:hover': {
                  backgroundColor: themeColors.buttonHover,
                },
              }),
            }}
          >
            Convert to Galpin
          </Button>
        </Box>
      )}

      <ConvertToGalpinModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={() => convertToGalpinMutation.mutate()}
        loading={convertToGalpinMutation.isPending}
      />
    </Card>
  )
}
